import type { ReactNode } from "react";
import { Navbar } from "./navbar";
import { Sidebar } from "./sidebar/sidebar/sidebar";
import { RightSidebar } from "./sidebar/rightSidebar";
import { Footer } from "./footer";
import { ConstellationBackground } from "./constellationBackground";
import { DrawerOverlay } from "./sidebar/drawerOverlay";
import { DrawerFabs } from "./sidebar/drawerFabs";
import { SidebarToggleProvider } from "../../../contexts/sidebar/SidebarToggleContext";
import { usePageTitle } from "../../../hooks/common/usePageTitle";
import { useAuth } from "../../../contexts/auth/Context";
import { decodeUserInfo } from "../../../utils/user";
import { useDrawer } from "../../../hooks/common/useDrawer";
import { useNotificationSocket } from "../../../hooks/notification/useNotificationSocket";

interface PageLayoutProps {
  children: ReactNode;
  pageTitle?: string;
}

export const PageLayout = ({ children, pageTitle }: PageLayoutProps) => {
  usePageTitle(pageTitle);
  useNotificationSocket();
  const { token } = useAuth();
  const { role } = decodeUserInfo(token);
  const normalizedRole = role ? role.trim().toUpperCase() : null;
  const isSuperAdmin = normalizedRole === "SUPERADMIN";

  const {
    isLeftOpen,
    isRightOpen,
    openLeft,
    openRight,
    closeDrawer,
    openDrawer,
  } = useDrawer();

  return (
    <SidebarToggleProvider>
      <div className="min-h-screen bg-global-page flex flex-col relative isolate">
        <ConstellationBackground />
        <Navbar />
        <div
          className={`${isSuperAdmin ? "flex flex-col" : "xl:flex xl:items-stretch"} flex-1`}
        >
          {!isSuperAdmin && <Sidebar />}
          <div
            className={`flex-1 min-w-0 flex flex-col ${isSuperAdmin ? "w-full max-w-full" : "content-max-width mx-auto"}`}
          >
            <div className="flex-1 space-y-compact px-3 sm:px-4 py-4 sm:py-5">
              {children}
            </div>
            <Footer />
          </div>
          {!isSuperAdmin && <RightSidebar />}
        </div>

        {!isSuperAdmin && (
          <>
            <DrawerFabs
              onOpenLeft={openLeft}
              onOpenRight={openRight}
              isAnyOpen={openDrawer !== null}
            />
            <DrawerOverlay
              isOpen={isLeftOpen}
              onClose={closeDrawer}
              side="left"
            >
              <Sidebar variant="drawer" onClose={closeDrawer} />
            </DrawerOverlay>
            <DrawerOverlay
              isOpen={isRightOpen}
              onClose={closeDrawer}
              side="right"
            >
              <RightSidebar variant="drawer" onClose={closeDrawer} />
            </DrawerOverlay>
          </>
        )}
      </div>
    </SidebarToggleProvider>
  );
};
